import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Check, Star, Building2, Users, Droplets, ArrowRight, TrendingUp, Target, Globe } from 'lucide-react';
import SEO from '../components/SEO';

const Pricing = () => {
  const pricingPlans = [
    {
      name: 'Starter',
      price: '10 000€',
      period: '',
      features: [
        '15 000 bouteilles d\'eau 33 cl distribuées',
        'Publicité sur les bouteilles',
        'QR code personnalisé',
        'Support client dédié',
        'Rapport d\'impact mensuel'
      ],
      color: 'sky',
      popular: false,
      wellContribution: '10%',
      wellsBuilt: '1',
      wellsNote: 'Nous complétons la somme restante pour la construction du puits',
      waterVolume: '495L',
      impactDescription: 'Idéal pour tester notre solution avec un impact social mesurable'
    },
    {
      name: 'Premium',
      price: '25 000€', 
      period: '',
      features: [
        '50 000 bouteilles d\'eau 33 cl distribuées',
        'Publicité sur les bouteilles',
        'QR code personnalisé',
        'Événements exclusifs',
        'Support client dédié',
        'Rapport d\'impact hebdomadaire'
      ],
      color: 'emerald',
      popular: true,
      wellContribution: '10%',
      wellsBuilt: '3',
      wellsNote: 'Nous complétons la somme restante pour la construction du puits',
      waterVolume: '1 650L',
      impactDescription: 'Parfait pour maximiser votre visibilité et impact RSE'
    },
    {
      name: 'Enterprise',
      price: 'Sur mesure',
      period: '',
      features: [
        'Volume personnalisé de bouteilles',
        'Événements sur mesure',
        'Account manager dédié',
        'Co-branding premium',
        'Distribution programmée',
        'Rapport d\'impact en temps réel',
        'Suivi analytics détaillé'
      ],
      color: 'purple',
      popular: false,
      wellContribution: '10%',
      wellsBuilt: 'Variable',
      wellsNote: 'Calcul selon votre investissement',
      waterVolume: 'Variable',
      impactDescription: 'Solution complète pour maximiser votre engagement RSE'
    }
  ];

  const impactStats = [
    {
      icon: Droplets,
      title: 'Puits d\'eau construits',
      value: '50+',
      description: 'Avec l\'Association Ulker\'s',
      color: 'sky'
    },
    {
      icon: Users,
      title: 'Personnes impactées',
      value: '10 000+',
      description: 'Accès à l\'eau potable',
      color: 'emerald'
    },
    {
      icon: Globe,
      title: 'Pays bénéficiaire',
      value: '1',
      description: 'Continent africain - Visitez @ulkers_association',
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Tarifs de Partenariat"
        description="Découvrez nos formules de partenariat publicitaire : Starter (10 000€), Premium (25 000€) et Entreprise (sur mesure). Chaque campagne finance des puits d'eau en Afrique."
        path="/tarifs"
      />
      {/* Header Section */}
      <section className="bg-gradient-to-br from-sky-900 via-sky-700 to-emerald-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              Nos Tarifs de Partenariat
            </h1>
            <p className="text-xl lg:text-2xl text-sky-100 mb-8">
              Choisissez la formule qui correspond à vos objectifs marketing et d'impact social
            </p>
            <div className="flex justify-center items-center space-x-2 text-sky-100">
              <Target className="h-5 w-5" />
              <span className="text-lg">Paiement intégral à la signature</span>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Notre Impact Humanitaire
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Chaque euro investi dans nos formules contribue directement à la construction de puits d'eau potable avec l'Association Ulker's
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactStats.map((stat, index) => (
              <Card key={index} className="p-8 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-0 text-center space-y-4">
                  <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-r from-${stat.color}-400 to-${stat.color === 'sky' ? 'emerald' : stat.color === 'emerald' ? 'sky' : 'sky'}-400 rounded-xl mx-auto`}>
                    <stat.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <h3 className="text-xl font-semibold text-gray-900">{stat.title}</h3>
                  <p className="text-gray-600">{stat.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Formules de Partenariat
            </h2>
            <p className="text-xl text-gray-600">
              Transparence totale sur l'impact de votre investissement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 items-start">
            {pricingPlans.map((plan, index) => (
              <div key={index} className="relative">
                <Card className={`h-full border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ${plan.popular ? 'ring-2 ring-emerald-200' : ''}`}>
                  <CardContent className="p-8 relative">
                    {/* Plan Header */}
                    <div className="text-center mb-8">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                        {plan.popular && (
                          <span className="bg-gradient-to-r from-emerald-400 to-sky-400 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            Populaire
                          </span>
                        )}
                      </div>
                      <div className="mb-4">
                        <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                        {plan.period && <span className="text-gray-600">{plan.period}</span>}
                      </div>
                      <p className="text-gray-600 text-sm">{plan.impactDescription}</p>
                    </div>

                    {/* Impact Metrics */}
                    <div className="mb-8 p-4 bg-gradient-to-r from-sky-50 to-emerald-50 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Impact Humanitaire</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-lg font-bold text-emerald-600">{plan.wellContribution}</div>
                          <div className="text-gray-600">Reversé aux puits</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-sky-600">{plan.wellsBuilt}</div>
                          <div className="text-gray-600">Puits construits</div>
                        </div>
                      </div>
                        {plan.wellsNote && (
                          <div className="mt-3 py-2 px-3 bg-emerald-50 rounded-lg text-center">
                            <span className="text-sm font-semibold text-emerald-700">{plan.wellsNote}</span>
                          </div>
                        )}
                    </div>

                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start space-x-3">
                          <Check className={`h-5 w-5 text-${plan.color}-500 mt-0.5 flex-shrink-0`} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    {plan.name === 'Enterprise' ? (
                      <Link 
                        to="/contact" 
                        className="block w-full bg-gradient-to-r from-purple-500 to-sky-500 hover:from-purple-600 hover:to-sky-600 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 group text-center"
                      >
                        Demander un devis personnalisé
                        <ArrowRight className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <Link 
                        to="/contact" 
                        className={`block w-full bg-gradient-to-r from-${plan.color}-500 to-${plan.color === 'sky' ? 'emerald' : 'sky'}-500 hover:from-${plan.color}-600 hover:to-${plan.color === 'sky' ? 'emerald' : 'sky'}-600 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 group text-center`}
                      >
                        Choisir cette formule
                        <ArrowRight className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Transparence totale sur l'utilisation de vos fonds
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-emerald-500 mt-1" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Distribution des fonds</h3>
                    <p className="text-gray-700">10% de votre investissement finance directement la construction de puits d'eau potable avec l'Association Ulker's sur le continent africain</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Target className="h-6 w-6 text-sky-500 mt-1" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Suivi de l'impact</h3>
                    <p className="text-gray-700">Recevez des rapports détaillés avec photos et géolocalisation des puits construits grâce à votre contribution</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Building2 className="h-6 w-6 text-purple-500 mt-1" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Impact RSE mesurable</h3>
                    <p className="text-gray-700">Intégrez cet impact dans vos rapports RSE avec des données précises et vérifiables</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-sky-50 to-emerald-50">
                <CardContent className="p-0">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto">
                      <Droplets className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Association Ulker's</h3>
                    <p className="text-gray-600">
                      Notre partenaire humanitaire certifié pour la construction de puits d'eau potable sur le continent africain. Suivez @ulkers_association sur Instagram pour voir l'impact de vos contributions.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-sky-900 via-sky-700 to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Prêt à lancer votre campagne solidaire ?
          </h2>
          <p className="text-xl text-sky-100 mb-8">
            Contactez-nous pour discuter de la formule qui correspond à vos objectifs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="bg-white text-sky-600 font-semibold py-4 px-8 rounded-lg hover:bg-sky-50 transition-colors duration-200 group"
            >
              Demander un devis
              <ArrowRight className="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link 
              to="/contact"
              className="border-2 border-white text-white font-semibold py-4 px-8 rounded-lg hover:bg-white hover:text-sky-600 transition-colors duration-200"
            >
              Planifier un appel
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;